import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import Page from '../../atoms/Page';
import Card from '../../atoms/Card';
import Button, { Variants, Colors } from '../../atoms/Button';
import FeedFloTextInput from '../../atoms/FeedFloTextInput';
import FeedFloDropDown from '../../atoms/FeedFloDropDown';
import { SearchIconSVG } from '../../atoms/Icons';
import WideBinSetStatusCard from '../../organisms/WideBinSetStatusCard';
import useUser from '../../utils/hooks/useUser';
import { useCensor, censorTypes } from '../../utils/hooks/useCensor';
import { processBinSetOrders } from '../../utils/orderUtils';
import dayjs from 'dayjs';
import './AllBinsPage.scss';

const ALL_BINS_GQL = gql`
  query AllBinsQuery($farmWhere: farm_bool_exp!, $farmOrderBy: [farm_order_by!]!, $limit: Int!, $offset: Int!) {
    farm(
      limit: $limit
      offset: $offset
      where: { _and: [$farmWhere, { deleted_at: { _is_null: true } }] }
      order_by: $farmOrderBy
    ) {
      id
      name
      organization {
        id
        name
      }
      features_setting: barn_settings(
        where: { type: { _eq: "features" }, version: { _eq: 0 }, deleted_at: { _is_null: true } }
      ) {
        settings
      }
      bin_sets(
        where: { deleted_at: { _is_null: true } }
        order_by: [{ bins_aggregate: { min: { name: asc } } }, { created_at: desc }]
      ) {
        id
        bins(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
          id
          name
          capacity_in_grams
          last_delivery: deliveries(
            where: { status: { _eq: "delivered" } }
            order_by: { delivered_at: desc }
            limit: 1
          ) {
            id
            status
            delivered_at
            ordered_at
            trip_id
          }
          scheduled_delivery: deliveries(
            where: { status: { _eq: "ordered" } }
            order_by: { delivered_at: desc }
            limit: 1
          ) {
            id
            status
            delivered_at
            ordered_at
            trip_id
          }
          recommended_delivery: deliveries(
            where: { status: { _eq: "recommended" } }
            order_by: { delivered_at: desc }
            limit: 1
          ) {
            id
            status
            delivered_at
            ordered_at
            trip_id
          }
          shipped_delivery: deliveries(
            where: { status: { _eq: "shipped" } }
            order_by: { delivered_at: desc }
            limit: 1
          ) {
            id
            status
            delivered_at
            ordered_at
            trip_id
          }
        }
        feed_lines(where: { deleted_at: { _is_null: true } }) {
          name
        }
        bin_set_level_predictions(
          where: {
            deleted_at: { _is_null: true },
            version: { _in: ["3.0"] },
          }
          order_by: { created_at: desc }
          limit: 1
        ) {
          created_at
          predicted_at
          bin_set_level_in_grams
          empty_at
          earliest_delivery_at
          runout_prevention_order_deadline_at
          runout_prevention_bin_set_level_in_grams
          earliest_delivery_bin_set_level_in_grams
          past_feed_inventory_time_series
          delivered_deliveries_projection_time_series
          scheduled_deliveries_projection_time_series
          recommended_deliveries_projection_time_series
          per_bin_past_feed_inventory_time_series
          per_bin_delivered_deliveries_projection_time_series
          per_bin_scheduled_deliveries_projection_time_series
          per_bin_recommended_deliveries_projection_time_series
          per_bin_level_in_grams
        }
      }
    }
  }
`;

function AllBinsPage() {
  const { user } = useUser();
  const { censor } = useCensor();
  const isMetric = user?.isMetric || false;
  const [searchFilter, setSearchFilter] = useState('');
  const [sortOption, setSortOption] = useState('alphabetical');
  const [allFarms, setAllFarms] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const PAGE_SIZE = 10;

  // Define the default farm filter (current hardcoded filter)
  const defaultFarmFilter = {
    bin_sets: { id: { _is_null: false } },
    barn_settings: {
      type: { _eq: 'features' },
      version: { _eq: 0 },
      settings: { _contains: { bin_inventory_enabled: true } },
      deleted_at: { _is_null: true },
    },
    deleted_at: { _is_null: true },
    name: {},
  };

  // Create dynamic farm filter based on search input
  const farmWhere = useMemo(() => {
    if (!searchFilter || searchFilter.trim() === '' || searchFilter.trim().length < 3) {
      return defaultFarmFilter;
    }

    return {
      bin_sets: { id: { _is_null: false } },
      barn_settings: {
        type: { _eq: 'features' },
        version: { _eq: 0 },
        settings: { _contains: { bin_inventory_enabled: true } },
        deleted_at: { _is_null: true },
      },
      deleted_at: { _is_null: true },
      name: { _ilike: `%${searchFilter}%` },
    };
  }, [searchFilter]);

  // Create dynamic order_by based on sort option
  const farmOrderBy = useMemo(() => {
    if (sortOption === 'first_to_empty') {
      // For "first to empty", we'll need to sort client-side since the GraphQL aggregate is complex
      // For now, just use alphabetical on server and sort client-side
      return [{ name: 'asc' }];
    }
    // Default alphabetical sorting
    return [{ name: 'asc' }];
  }, [sortOption]);

  // Add farm data processing similar to BarnListPage
  const processFarmDataForView = useCallback(
    (farm) => {
      return {
        ...farm,
        // Show org name if user has access to multiple orgs via any method (same logic as BarnListPage)
        showOrgName: user?.isStaff || user?.organizations?.length > 1 || user?.barns?.length > 1,
      };
    },
    [user?.isStaff, user?.organizations?.length, user?.barns?.length],
  );

  const { loading, error, data, fetchMore } = useQuery(ALL_BINS_GQL, {
    variables: { farmWhere, farmOrderBy, limit: PAGE_SIZE, offset: 0 },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const startDateTime = useMemo(() => dayjs().subtract(4, 'week'), []);
  const endDateTime = useMemo(() => dayjs().add(4, 'week'), []);

  // Update farms when data changes - let Apollo handle the cache
  useEffect(() => {
    if (data?.farm) {
      // Check if this is more data than we had before (indicating fetchMore worked)
      const newFarmCount = data.farm.length;
      const currentFarmCount = allFarms.length;

      // Only update if we actually have new data or if we're going from empty to having data
      if (newFarmCount > currentFarmCount || (currentFarmCount === 0 && newFarmCount > 0)) {
        // Process farm data to add showOrgName logic
        const processedFarms = data.farm.map(processFarmDataForView);
        setAllFarms(processedFarms);
        setOffset(newFarmCount);

        // Update hasMore based on the latest fetch result
        const lastFetchSize = newFarmCount - currentFarmCount;
        setHasMore(lastFetchSize % PAGE_SIZE === 0 || (currentFarmCount === 0 && newFarmCount === PAGE_SIZE));
      } else if (newFarmCount === 0 && currentFarmCount === 0) {
        // Both are empty - set hasMore to false to prevent further loading attempts
        setHasMore(false);
      }
    }
  }, [data, PAGE_SIZE, allFarms.length, processFarmDataForView]);

  // Reset pagination when search filter or sort changes
  useEffect(() => {
    setAllFarms([]);
    setOffset(0);
    setHasMore(true);
    setIsLoadingMore(false);
  }, [farmWhere, sortOption]);

  // Load more data function - simplified to rely on Apollo cache
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || loading) {
      return;
    }

    setIsLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          farmWhere,
          farmOrderBy,
          limit: PAGE_SIZE,
          offset: offset,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.farm?.length) {
            return prev;
          }

          return {
            ...prev,
            farm: [...(prev.farm || []), ...fetchMoreResult.farm],
          };
        },
      });
    } catch (err) {
      console.error('Error loading more farms:', err);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, loading, fetchMore, farmWhere, farmOrderBy, PAGE_SIZE, offset]);

  // Infinite scroll handler
  const handleScroll = useCallback(
    (event) => {
      const scrollContainer = event.target;
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const clientHeight = scrollContainer.clientHeight;

      // Trigger when within 500px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 500) {
        loadMore();
      }
    },
    [loadMore, hasMore, isLoadingMore, loading, offset, allFarms.length],
  );

  // Add scroll event listener to the correct container
  useEffect(() => {
    const scrollContainer = document.querySelector('.pageSpace.customScroll');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    } else {
      console.warn('Scroll container .pageSpace.customScroll not found');
      // Fallback to window scroll
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const onChangeSearchFilter = (text) => {
    setSearchFilter(text);
  };

  const onBlurSearchFilter = () => {
    // Handle search blur if needed
  };

  const onChangeSortOption = (item) => {
    setSortOption(item.id);
  };

  const sortOptions = useMemo(
    () => [
      {
        id: 'alphabetical',
        name: 'Alphabetical',
        selected: sortOption === 'alphabetical',
      },
      {
        id: 'first_to_empty',
        name: 'First to Empty',
        selected: sortOption === 'first_to_empty',
      },
    ],
    [sortOption],
  );

  const renderContent = () => {
    // Show error if there's an error and no data
    if (error && allFarms.length === 0) {
      return (
        <div className="AllBinsPage-error">
          <span>Error: {JSON.stringify(error)}</span>
        </div>
      );
    }

    // Show initial loading only if no farms are loaded yet
    if (loading && allFarms.length === 0) {
      return (
        <div className="AllBinsPage-loading">
          <span>Loading...</span>
        </div>
      );
    }

    // Show no results if we have searched but found nothing
    if (allFarms.length === 0 && searchFilter && !loading) {
      return (
        <div className="AllBinsPage-noResults">
          <h3>No Results Found</h3>
          <p>No farms or bins matched your search criteria for &ldquo;{searchFilter}&rdquo;</p>
        </div>
      );
    }

    return (
      <>
        {allFarms.map((farm) => (
          <Card key={farm.id} className="AllBinsPage-farmCard" contentClassName="AllBinsPage-farmCardContent">
            <h2 className="AllBinsPage-farmName">
              <Link to={`/b/${farm.id}`}>
                {censor(farm.name, censorTypes.barn)}
                {farm.showOrgName && farm.organization?.name && (
                  <span className="AllBinsPage-orgName">
                    {' ('}
                    {censor(farm.organization.name, censorTypes.organization)}
                    {')'}
                  </span>
                )}
              </Link>
            </h2>
            <div className="AllBinsPage-binSets">
              {farm.bin_sets.map((binSet) => {
                const { bins, order } = processBinSetOrders(binSet, censor);

                const feedLineNames = binSet.feed_lines.map((fl) => censor(fl.name, censorTypes.feedline));
                const prediction = binSet.bin_set_level_predictions[0] || {};
                const perBinChartData = {
                  pastByBinId: prediction.per_bin_past_feed_inventory_time_series || null,
                  deliveredByBinId: prediction.per_bin_delivered_deliveries_projection_time_series || null,
                  scheduledByBinId: prediction.per_bin_scheduled_deliveries_projection_time_series || null,
                  recommendedByBinId: prediction.per_bin_recommended_deliveries_projection_time_series || null,
                };

                return (
                  <WideBinSetStatusCard
                    key={binSet.id}
                    binSetId={binSet.id}
                    bins={bins}
                    perBinChartData={perBinChartData}
                    perBinLevelInGrams={prediction.per_bin_level_in_grams}
                    feedLineNames={feedLineNames}
                    binSetLevelInGrams={prediction.bin_set_level_in_grams}
                    predictedAt={prediction.predicted_at}
                    emptyAt={prediction.empty_at}
                    earliestDeliveryAt={prediction.earliest_delivery_at}
                    runout_prevention_order_deadline_at={prediction.runout_prevention_order_deadline_at}
                    runout_prevention_bin_set_level_in_grams={prediction.runout_prevention_bin_set_level_in_grams}
                    earliest_delivery_bin_set_level_in_grams={prediction.earliest_delivery_bin_set_level_in_grams}
                    isMetric={isMetric}
                    order={order}
                    lastUpdatedAt={prediction.created_at}
                    step_bin_level_history={prediction.past_feed_inventory_time_series || []}
                    projection_past_deliveries={prediction.delivered_deliveries_projection_time_series || []}
                    projection_with_scheduled={prediction.scheduled_deliveries_projection_time_series || []}
                    projection_with_recommended={prediction.recommended_deliveries_projection_time_series || []}
                    chartStartTime={startDateTime.unix()}
                    chartEndTime={endDateTime.unix()}
                  />
                );
              })}
            </div>
          </Card>
        ))}
        {(isLoadingMore || (loading && allFarms.length > 0)) && (
          <div className="AllBinsPage-loadingMore">
            <div className="AllBinsPage-spinner"></div>
            <span>Loading more farms...</span>
          </div>
        )}
        {/* Load More button as backup for infinite scroll */}
        {hasMore && !isLoadingMore && !loading && (
          <div className="AllBinsPage-loadMoreContainer">
            <Button
              variant={Variants.Pastel}
              color={Colors.Success}
              content="Load More Barns"
              onClick={loadMore}
              disabled={isLoadingMore}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <Page className="AllBinsPage" heading="All Bins" titleSegments={['All Bins']}>
      <div className="AllBinsPage-searchContainer">
        <FeedFloTextInput
          className="AllBinsPage-searchBarWrapper"
          inputClassName="AllBinsPage-searchBar"
          defaultText="Search by Barn Name"
          autoFocus={false}
          text={searchFilter}
          onChange={onChangeSearchFilter}
          onBlur={onBlurSearchFilter}
        />
        <SearchIconSVG className="AllBinsPage-searchIcon" />
        <FeedFloDropDown
          className="AllBinsPage-sortDropdown"
          list={sortOptions}
          defaultTitle="Sort by"
          onChange={onChangeSortOption}
        />
      </div>
      <div className="AllBinsPage-content">{renderContent()}</div>
    </Page>
  );
}

export default AllBinsPage;
